import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faUserMd, 
  faUserPlus, 
  faUsers, 
  faEdit, 
  faSave, 
  faTimes, 
  faCheck,
  faEnvelope,
  faCalendarAlt,
  faUser,
  faSpinner,
  faSearch,
  faTrash,
  faExclamationTriangle
} from '@fortawesome/free-solid-svg-icons';
import { adminAPI } from '../../services/api';
import { Skeleton } from '../Skeleton';
import './DoctorManagement.css';

interface Doctor {
  id: number;
  email: string;
  full_name: string;
  created_at: string;
  is_active: boolean;
  activation_token?: string;
  patient_count?: number; // Fixed: should match backend response
  is_invited?: boolean;
}

interface Patient {
  id: number;
  email: string;
  full_name: string;
  doctor_id?: number;
  is_active: boolean;
}

export const DoctorManagement: React.FC = () => {
  const [doctors, setDoctors] = useState<Doctor[]>([]);
  const [patients, setPatients] = useState<Patient[]>([]);
  const [loading, setLoading] = useState(true);
  const [patientsLoading, setPatientsLoading] = useState(false);
  
  // New doctor form
  const [showAddDoctorForm, setShowAddDoctorForm] = useState(false);
  const [newDoctorEmail, setNewDoctorEmail] = useState('');
  const [newDoctorName, setNewDoctorName] = useState('');
  const [addingDoctor, setAddingDoctor] = useState(false);
  
  // Patient assignment
  const [showPatientAssignment, setShowPatientAssignment] = useState(false);
  const [selectedDoctorId, setSelectedDoctorId] = useState<number | null>(null);
  const [patientSearchTerm, setPatientSearchTerm] = useState('');
  
  // Doctor editing
  const [editingDoctorId, setEditingDoctorId] = useState<number | null>(null);
  const [editDoctorEmail, setEditDoctorEmail] = useState('');
  const [editDoctorName, setEditDoctorName] = useState('');
  
  // Delete confirmation
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [doctorToDelete, setDoctorToDelete] = useState<Doctor | null>(null);

  useEffect(() => {
    loadDoctors();
    loadPatients();
  }, []);

  const loadDoctors = async () => {
    setLoading(true);
    try {
      const data = await adminAPI.getDoctors();
      setDoctors(data);
    } catch (error) {
      console.error('Error loading doctors:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadPatients = async () => {
    setPatientsLoading(true);
    try {
      const data = await adminAPI.getPatients();
      setPatients(data);
    } catch (error) {
      console.error('Error loading patients:', error);
    } finally {
      setPatientsLoading(false);
    }
  };

  const handleAddDoctor = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newDoctorEmail.trim() || !newDoctorName.trim()) return;

    setAddingDoctor(true);
    try {
      await adminAPI.inviteDoctor({
        email: newDoctorEmail.trim(),
        full_name: newDoctorName.trim()
      });
      
      setNewDoctorEmail('');
      setNewDoctorName('');
      setShowAddDoctorForm(false);
      loadDoctors();
    } catch (error) {
      console.error('Error creating doctor:', error);
      alert('Błąd podczas tworzenia konta lekarza. Sprawdź czy email nie jest już używany.');
    } finally {
      setAddingDoctor(false);
    }
  };

  const handleEditDoctor = (doctor: Doctor) => {
    setEditingDoctorId(doctor.id);
    setEditDoctorEmail(doctor.email);
    setEditDoctorName(doctor.full_name);
  };

  const handleSaveEdit = async (doctorId: number) => {
    try {
      await adminAPI.updateDoctor(doctorId, {
        full_name: editDoctorName.trim()
      });
      
      setEditingDoctorId(null);
      loadDoctors();
    } catch (error) {
      console.error('Error updating doctor:', error);
      alert('Błąd podczas aktualizacji danych lekarza.');
    }
  };

  const handleCancelEdit = () => {
    setEditingDoctorId(null);
    setEditDoctorEmail('');
    setEditDoctorName('');
  };

  const handleDeleteDoctor = async () => {
    if (!doctorToDelete) return;
    
    try {
      await adminAPI.deleteDoctor(doctorToDelete.id);
      setShowDeleteConfirm(false);
      setDoctorToDelete(null);
      loadDoctors();
      loadPatients(); // Refresh to update patient assignments
    } catch (error) {
      console.error('Error deleting doctor:', error);
      alert('Błąd podczas usuwania lekarza.');
    }
  };

  const handleAssignPatient = async (patientId: number, doctorId: number | null) => {
    try {
      await adminAPI.assignPatientToDoctor(patientId, doctorId || undefined);
      loadPatients();
      loadDoctors(); // Refresh to update patient counts
    } catch (error) {
      console.error('Error assigning patient:', error);
      alert('Błąd podczas przypisywania pacjenta.');
    }
  };

  const handleResendInvitation = async (doctorId: number) => {
    try {
      await adminAPI.resendDoctorInvitation(doctorId);
      alert('Zaproszenie zostało wysłane ponownie.');
    } catch (error) {
      console.error('Error resending doctor invitation:', error);
      alert('Błąd podczas wysyłania zaproszenia.');
    }
  };

  const filteredPatients = patients.filter(patient =>
    patient.full_name.toLowerCase().includes(patientSearchTerm.toLowerCase()) ||
    patient.email.toLowerCase().includes(patientSearchTerm.toLowerCase())
  );

  const getDoctorName = (doctorId: number | undefined) => {
    if (!doctorId) return 'Brak przypisania';
    const doctor = doctors.find(d => d.id === doctorId);
    return doctor ? doctor.full_name : 'Nieznany lekarz';
  };

  if (loading) {
    return (
      <div className="doctor-management">
        <div className="doctor-management-header">
          <h2><FontAwesomeIcon icon={faUserMd} className="icon" /> Zarządzanie Lekarzami</h2>
        </div>
        <div className="loading-container">
          <Skeleton height="200px" />
          <Skeleton height="200px" />
          <Skeleton height="200px" />
        </div>
      </div>
    );
  }

  return (
    <div className="doctor-management">
      <div className="doctor-management-header">
        <h2><FontAwesomeIcon icon={faUserMd} className="icon" /> Zarządzanie Lekarzami</h2>
        <button 
          className="btn btn-primary"
          onClick={() => setShowAddDoctorForm(true)}
        >
          <FontAwesomeIcon icon={faUserPlus} /> Dodaj Lekarza
        </button>
      </div>

      {/* Add Doctor Form */}
      {showAddDoctorForm && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h3>Dodaj Nowego Lekarza</h3>
              <button 
                className="btn-close"
                onClick={() => setShowAddDoctorForm(false)}
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>
            <form onSubmit={handleAddDoctor} className="add-doctor-form">
              <div className="form-group">
                <label>Email:</label>
                <input
                  type="email"
                  value={newDoctorEmail}
                  onChange={(e) => setNewDoctorEmail(e.target.value)}
                  required
                  placeholder="lekarz@przykład.pl"
                />
              </div>
              <div className="form-group">
                <label>Imię i Nazwisko:</label>
                <input
                  type="text"
                  value={newDoctorName}
                  onChange={(e) => setNewDoctorName(e.target.value)}
                  required
                  placeholder="Dr Jan Kowalski"
                />
              </div>
              <div className="form-actions">
                <button 
                  type="button" 
                  className="btn btn-secondary"
                  onClick={() => setShowAddDoctorForm(false)}
                >
                  Anuluj
                </button>
                <button 
                  type="submit" 
                  className="btn btn-primary"
                  disabled={addingDoctor}
                >
                  {addingDoctor ? <FontAwesomeIcon icon={faSpinner} spin /> : <FontAwesomeIcon icon={faCheck} />}
                  {addingDoctor ? 'Dodawanie...' : 'Dodaj'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Doctors List */}
      <div className="doctors-section">
        <h3><FontAwesomeIcon icon={faUsers} /> Lista Lekarzy ({doctors.length})</h3>
        
        {doctors.length === 0 ? (
          <div className="empty-state">
            <FontAwesomeIcon icon={faUserMd} className="empty-icon" />
            <p>Brak lekarzy w systemie</p>
            <button 
              className="btn btn-primary"
              onClick={() => setShowAddDoctorForm(true)}
            >
              Dodaj pierwszego lekarza
            </button>
          </div>
        ) : (
          <div className="doctors-grid">
            {doctors.map((doctor) => (
              <div key={doctor.id} className="doctor-card">
                <div className="doctor-header">
                  <div className="doctor-info">
                    <FontAwesomeIcon icon={faUserMd} className="doctor-icon" />
                    <div>
                      {editingDoctorId === doctor.id ? (
                        <input
                          type="text"
                          value={editDoctorName}
                          onChange={(e) => setEditDoctorName(e.target.value)}
                          className="edit-input"
                        />
                      ) : (
                        <h4>{doctor.full_name}</h4>
                      )}
                      {editingDoctorId === doctor.id ? (
                        <input
                          type="email"
                          value={editDoctorEmail}
                          onChange={(e) => setEditDoctorEmail(e.target.value)}
                          className="edit-input"
                        />
                      ) : (
                        <p className="doctor-email">{doctor.email}</p>
                      )}
                    </div>
                  </div>
                  <div className="doctor-actions">
                    {editingDoctorId === doctor.id ? (
                      <>
                        <button 
                          className="btn btn-sm btn-success"
                          onClick={() => handleSaveEdit(doctor.id)}
                        >
                          <FontAwesomeIcon icon={faSave} />
                        </button>
                        <button 
                          className="btn btn-sm btn-secondary"
                          onClick={handleCancelEdit}
                        >
                          <FontAwesomeIcon icon={faTimes} />
                        </button>
                      </>
                    ) : (
                      <>
                        <button 
                          className="btn btn-sm btn-secondary"
                          onClick={() => handleEditDoctor(doctor)}
                        >
                          <FontAwesomeIcon icon={faEdit} />
                        </button>
                        <button 
                          className="btn btn-sm btn-danger"
                          onClick={() => {
                            setDoctorToDelete(doctor);
                            setShowDeleteConfirm(true);
                          }}
                        >
                          <FontAwesomeIcon icon={faTrash} />
                        </button>
                      </>
                    )}
                  </div>
                </div>
                
                <div className="doctor-stats">
                  <div className="stat">
                    <FontAwesomeIcon icon={faUsers} />
                    <span>Pacjenci: {doctor.patient_count || 0}</span>
                  </div>
                  <div className="stat">
                    <FontAwesomeIcon icon={faCalendarAlt} />
                    <span>Utworzono: {new Date(doctor.created_at).toLocaleDateString('pl-PL')}</span>
                  </div>
                  <div className={`status ${doctor.is_active ? 'active' : 'inactive'}`}>
                    <FontAwesomeIcon icon={doctor.is_active ? faCheck : faTimes} />
                    <span>{doctor.is_active ? 'Aktywny' : 'Nieaktywny'}</span>
                  </div>
                </div>

                {!doctor.is_active && doctor.is_invited && (
                  <button 
                    className="btn btn-sm btn-primary resend-btn"
                    onClick={() => handleResendInvitation(doctor.id)}
                  >
                    <FontAwesomeIcon icon={faEnvelope} /> Wyślij ponownie zaproszenie
                  </button>
                )}

                <button 
                  className="btn btn-sm btn-outline assign-patients-btn"
                  onClick={() => {
                    setSelectedDoctorId(doctor.id);
                    setShowPatientAssignment(true);
                  }}
                >
                  <FontAwesomeIcon icon={faUser} /> Zarządzaj pacjentami
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Patient Assignment Modal */}
      {showPatientAssignment && (
        <div className="modal-overlay">
          <div className="modal large">
            <div className="modal-header">
              <h3>
                <FontAwesomeIcon icon={faUsers} /> Przypisywanie Pacjentów
                {selectedDoctorId && (
                  <span className="doctor-name">
                    - {doctors.find(d => d.id === selectedDoctorId)?.full_name}
                  </span>
                )}
              </h3>
              <button 
                className="btn-close"
                onClick={() => {
                  setShowPatientAssignment(false);
                  setSelectedDoctorId(null);
                  setPatientSearchTerm('');
                }}
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>
            
            <div className="patient-assignment-content">
              <div className="search-bar">
                <FontAwesomeIcon icon={faSearch} />
                <input
                  type="text"
                  placeholder="Szukaj pacjenta..."
                  value={patientSearchTerm}
                  onChange={(e) => setPatientSearchTerm(e.target.value)}
                />
              </div>

              {patientsLoading ? (
                <div className="loading-patients">
                  <FontAwesomeIcon icon={faSpinner} spin />
                  <span>Ładowanie pacjentów...</span>
                </div>
              ) : (
                <div className="patients-list">
                  {filteredPatients.map((patient) => (
                    <div key={patient.id} className="patient-assignment-item">
                      <div className="patient-info">
                        <FontAwesomeIcon icon={faUser} />
                        <div>
                          <h4>{patient.full_name}</h4>
                          <p>{patient.email}</p>
                        </div>
                      </div>
                      
                      <div className="assignment-info">
                        <span className="current-assignment">
                          Przypisany do: {getDoctorName(patient.doctor_id)}
                        </span>
                        
                        <select
                          value={patient.doctor_id || ''}
                          onChange={(e) => {
                            const newDoctorId = e.target.value ? parseInt(e.target.value) : null;
                            handleAssignPatient(patient.id, newDoctorId);
                          }}
                          className="doctor-select"
                        >
                          <option value="">Brak przypisania (Admin)</option>
                          {doctors.map((doctor) => (
                            <option key={doctor.id} value={doctor.id}>
                              {doctor.full_name}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && doctorToDelete && (
        <div className="modal-overlay">
          <div className="modal small">
            <div className="modal-header">
              <h3>
                <FontAwesomeIcon icon={faExclamationTriangle} className="warning-icon" />
                Potwierdź Usunięcie
              </h3>
            </div>
            <div className="modal-content">
              <p>
                Czy na pewno chcesz usunąć lekarza <strong>{doctorToDelete.full_name}</strong>?
              </p>
              <p className="warning-text">
                Ta operacja jest nieodwracalna. Wszyscy pacjenci przypisani do tego lekarza 
                zostaną przeniesieni do zarządzania przez administratora.
              </p>
            </div>
            <div className="modal-actions">
              <button 
                className="btn btn-secondary"
                onClick={() => {
                  setShowDeleteConfirm(false);
                  setDoctorToDelete(null);
                }}
              >
                Anuluj
              </button>
              <button 
                className="btn btn-danger"
                onClick={handleDeleteDoctor}
              >
                <FontAwesomeIcon icon={faTrash} /> Usuń
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
